/* eslint-disable import/no-cycle */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Popconfirm, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { QuestionCircleOutlined } from '@ant-design/icons';
import PageHeader from '../../components/PageHeader/PageHeader';
import ContentCustom from '../../components/ContentCustom/ContentCustom';
import ChoiceOfferModal from './ChoiceOfferModal';
import MRIMultiOffersModal, { MRI_MODAL_PRODUCTS } from './MRIOffersModal';
import PNOMultiOffersModal, { PNO_MODAL_PRODUCTS } from './PNOOffersModal';
import RcngawkModal from './RcngawkModal';
import useHandleResize from '../../utils/HandleResize';
import useAuthContext from '../../contexts/AuthContext';
import { routes } from '../../utils/constants/routes';

const ProductsQuote = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { productAccess, user } = useAuthContext();
  const { filter } = useParams();
  const { width } = useHandleResize();

  const [openChoiceOfferModal, setOpenChoiceOfferModal] = useState(false);
  const [openMriOfferModal, setOpenMriOfferModal] = useState(false);
  const [openPnoOfferModal, setOpenPnoOfferModal] = useState(false);
  const [openRcngawkModal, setOpenRcngawkModal] = useState(false);
  const [productSelected, setProductSelected] = useState();

  // Default products data
  const defaultProducts = useMemo(
    () => [
      {
        title: 'association',
        description: 'Assurance association',
        image: '/images/quotes-img/asso.jpg'
      },
      {
        title: 'mrp',
        description: 'Multirisque Professionnelle',
        image: '/images/quotes-img/mrb.jpg'
      },
      {
        title: 'rcp',
        description: 'Responsabilité Civile Professionnelle',
        image: '/images/quotes-img/rcp.jpg'
      },
      {
        title: 'mri',
        description: 'Multirisque Immeuble',
        image: '/images/quotes-img/mri.jpg'
      },
      {
        title: 'prevoyance',
        description: 'Assurance prévoyance',
        image: '/images/quotes-img/prevoyance.jpg'
      },
      {
        title: 'pno',
        titleLink: 'pnoml',
        path: `${routes.QUOTES}/pnoml`,
        description: t('pno.title'),
        highlight: false,
        productType: 'pno',
        image: '/images/quotes-img/pno.jpg'
      },
      {
        title: 'pne',
        titleLink: 'pnoml',
        path: `${routes.QUOTES}/pnoml`,
        description: t('pne.title'),
        highlight: false,
        productType: 'pne',
        image: '/images/quotes-img/pne.jpg'
      }
    ],
    [t]
  );

  // Compute displayed products based on user permissions
  const displayedProducts = useMemo(() => {
    const hasAccess =
      user?.permissionManagement?.products_access &&
      user.permissionManagement.products_access.length > 0;

    if (!hasAccess) {
      return defaultProducts;
    }

    if (filter && filter !== 'all') {
      return productAccess.filter((p) => p?.productType?.includes(filter));
    }

    if (filter === 'all' && productAccess.length > 0) {
      return productAccess.sort((p1, p2) => p1.order - p2.order);
    }

    return defaultProducts;
  }, [user, filter, productAccess, defaultProducts]);

  // Reset product selection when closing modal
  useEffect(() => {
    if (!openChoiceOfferModal) {
      setProductSelected(undefined);
    }
  }, [openChoiceOfferModal]);

  // Open appropriate modal based on product selected
  useEffect(() => {
    if (!productSelected) return;

    if (MRI_MODAL_PRODUCTS.includes(productSelected)) {
      setOpenMriOfferModal(true);
    } else if (PNO_MODAL_PRODUCTS.includes(productSelected)) {
      setOpenPnoOfferModal(true);
    } else if (productSelected === 'RCNGAWK') {
      setOpenRcngawkModal(true);
    } else {
      setOpenChoiceOfferModal(true);
    }
  }, [productSelected]);

  // Handle product selection from location state
  useEffect(() => {
    if (location.state?.product) {
      setProductSelected(location.state.product.toUpperCase());
    }
  }, [location]);

  // Render appropriate button for each product
  const computeCreateQuoteButton = useCallback(
    (product) => {
      if (product.upcoming) {
        return (
          <Link
            type="primary"
            className="quote-card-button disabled"
            to={{ pathname: '/quotes/productsquote' }}
            title="Patience, ce produit arrive bientôt"
            disabled
          >
            <span>À venir</span>
          </Link>
        );
      }

      // Products with modal selection
      if (['rcp', 'mrp', 'mrb', 'rcngawk'].includes(product.title)) {
        return (
          <Button
            type="primary"
            className="product-quote-button"
            style={{ borderRadius: '10px 0 0 0' }}
            onClick={() =>
              setProductSelected(
                product.title === 'mrb' ? 'MRPB' : product.title.toUpperCase()
              )
            }
          >
            VOIR
          </Button>
        );
      }

      // MRP with confirmation
      if (product.title === 'mrpsm') {
        return (
          <Popconfirm
            title={t('quotes.productsquote.confirmMRP.title')}
            okText={t('quotes.productsquote.confirmMRP.okText')}
            cancelText={t('quotes.productsquote.confirmMRP.cancelText')}
            cancelButtonProps={{ type: 'primary' }}
            onConfirm={() =>
              navigate(
                `/quotes/mrp/create${
                  product.title === 'mrp' ? '?mrpT=any&sup=cm' : '?mrpT=any'
                }`
              )
            }
            onCancel={() =>
              navigate(
                `/quotes/mrp/create${
                  product.title === 'mrp'
                    ? '?mrpT=office&sup=cm'
                    : '?mrpT=office'
                }`
              )
            }
            style={{ color: '#fff', margin: 'auto', display: 'flex' }}
          >
            <Button
              type="primary"
              className="product-quote-button"
              style={{ borderRadius: '10px 0 0 0' }}
            >
              VOIR
            </Button>
          </Popconfirm>
        );
      }

      // Association with confirmation
      if (product.title === 'association') {
        return (
          <Popconfirm
            title={t('quotes.productsquote.confirmASSO.title')}
            okText={t('quotes.productsquote.confirmASSO.okText')}
            cancelText={t('quotes.productsquote.confirmASSO.cancelText')}
            cancelButtonProps={{ type: 'primary' }}
            onConfirm={() =>
              navigate(`/quotes/${product.title}/create?assoT=localRc`)
            }
            onCancel={() =>
              navigate(`/quotes/${product.title}/create?assoT=rc`)
            }
            icon={
              <QuestionCircleOutlined style={{ color: 'var(--clientColor)' }} />
            }
            style={{ color: '#fff', margin: 'auto', display: 'flex' }}
          >
            <Button
              type="primary"
              className="product-quote-button"
              style={{ borderRadius: '10px 0 0 0' }}
            >
              VOIR
            </Button>
          </Popconfirm>
        );
      }

      // MRI products
      if (MRI_MODAL_PRODUCTS.includes(product.title.toUpperCase())) {
        return (
          <Button
            type="primary"
            className="product-quote-button"
            style={{ borderRadius: '10px 0 0 0' }}
            onClick={() => setOpenMriOfferModal(true)}
          >
            VOIR
          </Button>
        );
      }

      // PNO products
      if (PNO_MODAL_PRODUCTS.includes(product.title.toUpperCase())) {
        return (
          <Button
            type="primary"
            className="product-quote-button"
            style={{ borderRadius: '10px 0 0 0' }}
            onClick={() => setOpenPnoOfferModal(true)}
          >
            VOIR
          </Button>
        );
      }

      // Default direct link
      return (
        <Link
          type="primary"
          className="quote-card-button"
          title={`Faire un devis (${product.title.toLowerCase()})`}
          to={{
            pathname: `/quotes/${
              product.titleLink ? product.titleLink : product.title
            }/create`,
            search:
              product.title === 'mrb'
                ? '?mrpT=office&sup=cm'
                : product.title === 'rcp'
                  ? '?sup=cm'
                  : ''
          }}
        >
          <span>VOIR</span>
        </Link>
      );
    },
    [navigate, t]
  );

  return (
    <>
      <ChoiceOfferModal
        openChoiceOfferModal={openChoiceOfferModal}
        setOpenChoiceOfferModal={setOpenChoiceOfferModal}
        productSelected={productSelected}
      />
      <MRIMultiOffersModal
        isOpen={openMriOfferModal}
        onClose={() => setOpenMriOfferModal(false)}
      />
      <PNOMultiOffersModal
        isOpen={openPnoOfferModal}
        onClose={() => setOpenPnoOfferModal(false)}
      />
      <RcngawkModal
        isOpen={openRcngawkModal}
        onClose={() => setOpenRcngawkModal(false)}
      />
      <PageHeader title={t('quotes.productsquote.title_header')} />
      <ContentCustom>
        <Typography.Title level={4}>
          {t('quotes.productsquote.title_content')}
        </Typography.Title>
        <Row
          gutter={[16, 16]}
          style={{
            width: width < 1900 ? '100%' : '80%',
            margin: 'auto'
          }}
        >
          {displayedProducts.map((item) => (
            <Col key={item.title} xs={24} sm={24} md={12} lg={8} xl={8}>
              <div
                className="quote-card-item-background"
                style={{
                  backgroundImage: `url(${item.image})`
                }}
              >
                <div className="quote-card-item-container">
                  <div className="quote-card-title">{item.description}</div>
                  <div className="quote-card-item-container-button">
                    {computeCreateQuoteButton(item)}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </ContentCustom>
    </>
  );
};

export default ProductsQuote;
